import { z } from 'zod'

/**
 * Demand-intelligence contracts (Epic 8).
 *
 * The governing rule for every type here: **ranges, never point estimates**, always with the
 * sample size that produced them. A merchant who commits $40k of stock on a number that turns
 * out to be a guess will not trust anything the app says again, and will say so publicly.
 */

/** Bumped whenever the model changes. Stored on every computed figure so a number that moved between months can be explained. */
export const DEMAND_MODEL_VERSION = '2026-08-23.1'

export const confidenceSchema = z.enum(['none', 'low', 'medium', 'high'])
export type Confidence = z.infer<typeof confidenceSchema>

export const demandValuationSchema = z.object({
  variantId: z.string(),
  productId: z.string(),
  modelVersion: z.string(),
  computedAt: z.number().int(),

  waitingSubscribers: z.number().int(),
  /** After cross-variant dedupe and recency decay. The basis of the dollar figure. */
  effectiveDemand: z.number(),

  priceMinor: z.number().int(),
  currency: z.string(),

  conversionRate: z.number(),
  /** Whose history the rate came from. Surfaced in the UI; it is what confidence means. */
  rateSource: z.enum(['shop', 'platform', 'category']),
  rateSampleSize: z.number().int(),

  valueLowMinor: z.number().int(),
  valueMidMinor: z.number().int(),
  valueHighMinor: z.number().int(),
  confidence: confidenceSchema,
})

export type DemandValuation = z.infer<typeof demandValuationSchema>

export const variantMixEntrySchema = z.object({
  variantId: z.string(),
  variantTitle: z.string().nullable(),
  waitingSubscribers: z.number().int(),
  /** Share of this product's waiting demand, 0–1. */
  share: z.number(),
})

/**
 * The recommendation row (SW-802).
 *
 * `variantMix` is the feature that has no substitute anywhere else: historical sales data is
 * censored; it records what the merchant had stock of, not what shoppers wanted, and the
 * waitlist is uncensored demand. "Your waitlist is 40% XL but you order 15% XL" cannot be
 * derived from Shopify reports, a spreadsheet, or a competitor's dashboard.
 */
export const restockRecommendationSchema = z.object({
  productId: z.string(),
  productTitle: z.string().nullable(),
  imageUrl: z.string().nullable(),
  vendor: z.string().nullable(),

  waitingSubscribers: z.number().int(),
  demandValueMidMinor: z.number().int(),
  demandValueLowMinor: z.number().int(),
  demandValueHighMinor: z.number().int(),
  currency: z.string(),
  confidence: confidenceSchema,

  variantMix: z.array(variantMixEntrySchema),
  recommendedQuantities: z.array(
    z.object({
      variantId: z.string(),
      variantTitle: z.string().nullable(),
      recommendedQty: z.number().int(),
      waitingSubscribers: z.number().int(),
    }),
  ),

  /**
   * Waitlist signups scaled by the shop's observed capture rate. Loudly labelled an estimate
   * everywhere it surfaces: if 3% of sold-out-page visitors join, 200 signups implies ~6,600
   * interested shoppers, and the uncertainty on that is large.
   */
  extrapolatedDemand: z.object({
    captureRate: z.number(),
    captureRateSource: z.enum(['shop', 'override', 'platform_prior']),
    estimatedInterestedShoppers: z.number().int(),
  }),

  /**
   * Cost per item, where the merchant has entered one. Exposed so the restock screen can
   * prefill its field, without it, editing a cost means retyping one you cannot see.
   */
  costPerItemMinor: z.number().int().nullable(),
  marginMinor: z.number().int().nullable(),
  /** Plain-English "why this ranking?" for the row. Every row has one; none are optional. */
  reasoning: z.array(z.string()),
  rank: z.number().int(),
})

export type RestockRecommendation = z.infer<typeof restockRecommendationSchema>

export const stockoutReportRowSchema = z.object({
  variantId: z.string(),
  productId: z.string(),
  productTitle: z.string().nullable(),
  variantTitle: z.string().nullable(),
  stockoutCount: z.number().int(),
  totalDaysOutOfStock: z.number(),
  shareOfPeriodOutOfStock: z.number(),
  estimatedLostRevenueMinor: z.number().int(),
  currency: z.string(),
  /** Says the reorder *point* is wrong, which is a different insight from reorder quantity. */
  chronic: z.boolean(),
})

export type StockoutReportRow = z.infer<typeof stockoutReportRowSchema>
